import api from './api';
import type {
  PaginatedResponse,
  DashboardStats,
  UserRole,
} from '@/types';

// Types for Admin Service
export interface AdminDashboardStats extends DashboardStats {
  revenueThisMonth: number;
  revenueTrend: number;
  tenantsTrend: number;
  usersTrend: number;
  signupsLast7Days: { date: string; count: number }[];
  systemHealth: {
    api: 'healthy' | 'degraded' | 'down';
    database: 'healthy' | 'degraded' | 'down';
    storage: 'healthy' | 'degraded' | 'down';
    queue: 'healthy' | 'degraded' | 'down';
  };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  tenantId: string;
  tenantName: string;
  avatar?: string;
  phone?: string;
  isActive: boolean;
  lastLoginAt?: string;
  walletBalance?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserFilters {
  search?: string;
  role?: UserRole;
  status?: 'active' | 'inactive';
  tenantId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  plan: string;
  status: string;
  userCount: number;
  maxUsers: number;
  walletBalance: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminTenantFilters {
  search?: string;
  plan?: string;
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TopupRequestAdmin {
  id: string;
  tenantId: string;
  tenantName: string;
  amount: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  paymentMethod: string;
  paymentReference?: string;
  proofUrl?: string;
  notes?: string;
  userCurrentBalance?: number;
  requestedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  processedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  processedAt?: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'APPROVE' | 'REJECT' | 'ACTIVATE' | 'DEACTIVATE';
  tableName: string;
  recordId: string;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogFilters {
  action?: string;
  tableName?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface AdminSettings {
  general: {
    platformName: string;
    supportEmail: string;
    supportPhone: string;
    address: string;
  };
  billing: {
    gstPercentage: number;
    tdsPercentage: number;
    qLeadCost: number;
    minTopupAmount: number;
    maxTopupAmount: number;
  };
  notifications: {
    welcomeEmailEnabled: boolean;
    topupApprovalEmailEnabled: boolean;
    passwordResetEmailEnabled: boolean;
    lowBalanceAlertEnabled: boolean;
    lowBalanceThreshold: number;
  };
  security: {
    minPasswordLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    sessionTimeoutMinutes: number;
    maxLoginAttempts: number;
  };
}

export interface ActivityItem {
  id: string;
  type: 'signup' | 'topup_approved' | 'topup_rejected' | 'tenant_created' | 'user_activated' | 'user_deactivated';
  title: string;
  description: string;
  userId?: string;
  userName?: string;
  tenantId?: string;
  tenantName?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

const ADMIN_ENDPOINTS = {
  // Dashboard
  DASHBOARD_STATS: '/admin/stats',
  RECENT_ACTIVITY: '/admin/activity',

  // Users
  USERS: '/admin/users',
  USER_DETAIL: (id: string) => `/admin/users/${id}`,
  USER_ACTIVATE: (id: string) => `/admin/users/${id}/activate`,
  USER_DEACTIVATE: (id: string) => `/admin/users/${id}/deactivate`,
  USER_RESET_PASSWORD: (id: string) => `/admin/users/${id}/reset-password`,
  USER_ACTIVITY: (id: string) => `/admin/users/${id}/activity`,

  // Tenants
  TENANTS: '/admin/tenants',
  TENANT_DETAIL: (id: string) => `/admin/tenants/${id}`,
  TENANT_SUSPEND: (id: string) => `/admin/tenants/${id}/suspend`,
  TENANT_ACTIVATE: (id: string) => `/admin/tenants/${id}/activate`,
  TENANT_USAGE: (id: string) => `/admin/tenants/${id}/usage`,
  TENANT_BILLING: (id: string) => `/admin/tenants/${id}/billing`,

  // Topup requests
  TOPUP_REQUESTS: '/wallet/admin/pending-topups',
  TOPUP_APPROVE: (id: string) => `/wallet/admin/topups/${id}/approve`,
  TOPUP_REJECT: (id: string) => `/wallet/admin/topups/${id}/reject`,

  // Audit logs
  AUDIT_LOGS: '/admin/audit-logs',
  AUDIT_LOG_EXPORT: '/admin/audit-logs/export',

  // Settings
  SETTINGS: '/admin/settings',
};

export const adminService = {
  // Dashboard Stats
  async getDashboardStats(): Promise<AdminDashboardStats> {
    const response = await api.get<AdminDashboardStats>(ADMIN_ENDPOINTS.DASHBOARD_STATS);
    return response.data;
  },

  async getRecentActivity(limit: number = 10): Promise<ActivityItem[]> {
    const response = await api.get<ActivityItem[]>(`${ADMIN_ENDPOINTS.RECENT_ACTIVITY}?limit=${limit}`);
    return response.data;
  },

  // Users
  async getUsers(filters: AdminUserFilters = {}): Promise<PaginatedResponse<AdminUser>> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await api.get<PaginatedResponse<AdminUser>>(
      `${ADMIN_ENDPOINTS.USERS}?${params.toString()}`
    );
    return response.data;
  },

  async getUserById(id: string): Promise<AdminUser> {
    const response = await api.get<AdminUser>(ADMIN_ENDPOINTS.USER_DETAIL(id));
    return response.data;
  },

  async updateUser(id: string, data: Partial<AdminUser>): Promise<AdminUser> {
    const response = await api.put<AdminUser>(ADMIN_ENDPOINTS.USER_DETAIL(id), data);
    return response.data;
  },

  async activateUser(id: string): Promise<AdminUser> {
    const response = await api.post<AdminUser>(ADMIN_ENDPOINTS.USER_ACTIVATE(id));
    return response.data;
  },

  async deactivateUser(id: string): Promise<AdminUser> {
    const response = await api.post<AdminUser>(ADMIN_ENDPOINTS.USER_DEACTIVATE(id));
    return response.data;
  },

  async resetPassword(id: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(ADMIN_ENDPOINTS.USER_RESET_PASSWORD(id));
    return response.data;
  },

  async getUserActivity(id: string, page: number = 1, limit: number = 20): Promise<PaginatedResponse<AuditLog>> {
    const response = await api.get<PaginatedResponse<AuditLog>>(
      `${ADMIN_ENDPOINTS.USER_ACTIVITY(id)}?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  // Tenants
  async getTenants(filters: AdminTenantFilters = {}): Promise<PaginatedResponse<AdminTenant>> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.plan) params.append('plan', filters.plan);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await api.get<PaginatedResponse<AdminTenant>>(
      `${ADMIN_ENDPOINTS.TENANTS}?${params.toString()}`
    );
    return response.data;
  },

  async getTenantById(id: string): Promise<AdminTenant> {
    const response = await api.get<AdminTenant>(ADMIN_ENDPOINTS.TENANT_DETAIL(id));
    return response.data;
  },

  async updateTenant(id: string, data: Partial<AdminTenant>): Promise<AdminTenant> {
    const response = await api.put<AdminTenant>(ADMIN_ENDPOINTS.TENANT_DETAIL(id), data);
    return response.data;
  },

  async suspendTenant(id: string, reason: string): Promise<AdminTenant> {
    const response = await api.post<AdminTenant>(ADMIN_ENDPOINTS.TENANT_SUSPEND(id), { reason });
    return response.data;
  },

  async activateTenant(id: string): Promise<AdminTenant> {
    const response = await api.post<AdminTenant>(ADMIN_ENDPOINTS.TENANT_ACTIVATE(id));
    return response.data;
  },

  async getTenantUsage(id: string): Promise<{ leads: number; calls: number; storage: number }> {
    const response = await api.get<{ leads: number; calls: number; storage: number }>(
      ADMIN_ENDPOINTS.TENANT_USAGE(id)
    );
    return response.data;
  },

  async getTenantBillingHistory(id: string, page: number = 1): Promise<PaginatedResponse<{ id: string; amount: number; type: string; date: string; description: string }>> {
    const response = await api.get<PaginatedResponse<{ id: string; amount: number; type: string; date: string; description: string }>>(
      `${ADMIN_ENDPOINTS.TENANT_BILLING(id)}?page=${page}`
    );
    return response.data;
  },

  // Topup Requests
  async getTopupRequests(
    status?: 'PENDING' | 'APPROVED' | 'REJECTED',
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedResponse<TopupRequestAdmin>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await api.get<PaginatedResponse<TopupRequestAdmin>>(
      `${ADMIN_ENDPOINTS.TOPUP_REQUESTS}?${params.toString()}`
    );
    return response.data;
  },

  async approveTopup(id: string, notes?: string): Promise<TopupRequestAdmin> {
    const response = await api.post<TopupRequestAdmin>(
      ADMIN_ENDPOINTS.TOPUP_APPROVE(id),
      { notes }
    );
    return response.data;
  },

  async rejectTopup(id: string, reason: string): Promise<TopupRequestAdmin> {
    const response = await api.post<TopupRequestAdmin>(
      ADMIN_ENDPOINTS.TOPUP_REJECT(id),
      { reason }
    );
    return response.data;
  },

  // Audit Logs
  async getAuditLogs(filters: AuditLogFilters = {}): Promise<PaginatedResponse<AuditLog>> {
    const params = new URLSearchParams();

    if (filters.action) params.append('action', filters.action);
    if (filters.tableName) params.append('tableName', filters.tableName);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get<PaginatedResponse<AuditLog>>(
      `${ADMIN_ENDPOINTS.AUDIT_LOGS}?${params.toString()}`
    );
    return response.data;
  },

  async exportAuditLogs(filters: AuditLogFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.action) params.append('action', filters.action);
    if (filters.tableName) params.append('tableName', filters.tableName);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(
      `${ADMIN_ENDPOINTS.AUDIT_LOG_EXPORT}?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Settings
  async getSettings(): Promise<AdminSettings> {
    const response = await api.get<AdminSettings>(ADMIN_ENDPOINTS.SETTINGS);
    return response.data;
  },

  async updateSettings(data: Partial<AdminSettings>): Promise<AdminSettings> {
    const response = await api.put<AdminSettings>(ADMIN_ENDPOINTS.SETTINGS, data);
    return response.data;
  },

  // Export Users to CSV
  async exportUsersToCSV(filters: AdminUserFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.tenantId) params.append('tenantId', filters.tenantId);

    const response = await api.get(
      `${ADMIN_ENDPOINTS.USERS}/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};

export default adminService;
